import { LockKeyhole, LogOut, Plus } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export function WorkspaceUnavailable({
  bootstrapError,
  onRetry,
}: {
  bootstrapError?: string | null;
  onRetry?: () => Promise<void>;
}) {
  const { workspaces, createWorkspace, workspaceSwitching, logout } = useAuth();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const ownsWorkspace = workspaces.some((workspace) => workspace.role === "owner");
  const suspended = workspaces.filter((workspace) => workspace.status === "suspended");

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      await createWorkspace(name.trim());
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "Could not create workspace.");
    }
  };

  const retry = async () => {
    if (!onRetry || retrying) return;
    setError(null);
    setRetrying(true);
    try {
      await onRetry();
    } catch (retryError) {
      setError(
        retryError instanceof Error
          ? retryError.message
          : "Could not load your workspace.",
      );
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary px-5 py-12">
      <div className="mx-auto max-w-lg rounded-2xl border bg-background p-7 shadow-sm sm:p-9">
        <div className="mb-6 flex h-11 w-11 items-center justify-center rounded-xl bg-muted">
          <LockKeyhole className="h-5 w-5 text-muted-foreground" />
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {bootstrapError
            ? "We couldn't load your workspace"
            : "No workspace is available"}
        </h1>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          {bootstrapError
            ? "Your account and work are safe. Check your connection and try again."
            : "Your account is still here. Access to the workspaces below is paused until their owner restores a team-eligible plan or seat."}
        </p>
        {bootstrapError && (
          <div className="mt-6">
            <Button onClick={() => void retry()} disabled={!onRetry || retrying}>
              {retrying ? "Trying again…" : "Try again"}
            </Button>
            <p className="mt-2 text-xs text-muted-foreground">
              {error || bootstrapError}
            </p>
          </div>
        )}
        {suspended.length > 0 && (
          <div className="mt-6 divide-y rounded-xl border">
            {suspended.map((workspace) => (
              <div key={workspace.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <span className="truncate text-sm font-medium">{workspace.name}</span>
                <span className="text-xs text-muted-foreground">Unavailable</span>
              </div>
            ))}
          </div>
        )}
        {!bootstrapError && (
          <form onSubmit={submit} className="mt-7 rounded-xl bg-brand/5 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Plus className="h-4 w-4 text-brand-ink" />
              Create a workspace
            </div>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              {ownsWorkspace
                ? "This workspace will have separate credits and billing and starts without trial credits."
                : "This is separate from workspaces you joined and includes your one-time free trial."}
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Your workspace name"
                maxLength={60}
              />
              <Button type="submit" disabled={!name.trim() || workspaceSwitching}>
                {workspaceSwitching ? "Creating…" : "Create"}
              </Button>
            </div>
            {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
          </form>
        )}
        <Button variant="ghost" className="mt-6 px-0 text-muted-foreground" onClick={() => void logout()}>
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </Button>
      </div>
    </div>
  );
}
